import React, { createContext, useContext, useState, useCallback } from "react";
import { getUsers, User } from "../services/userService";
import { useNotification } from "./NotificationContext";

interface UserContextType {
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError("Failed to fetch users");
      showError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  return (
    <UserContext.Provider
      value={{
        users,
        loading,
        error,
        fetchUsers,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
